import { AppMRTGrid } from "src/components/Grid";
import { createMRTColumnHelper, MRT_PaginationState } from "material-react-table";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { IngredientList, useGetAllIngredient } from "src/api/client-api/ingredients/useGetAllIngredients";
import { GridLink } from "src/components/Grid/GridLink";
import { GridText } from "src/components/Grid/GridText";
import { SupplierFormIngredientSupplier } from "../../../SupplierForm.types";
import { URLHandler } from "src/services/URLHandler";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { isLinkRemovableBy } from "src/pages/Supplier/supplierLinksSync";
import { useCompanyMetadata } from "src/context/Company/Metadata/useCompanyMetadata";
import type { SupplierFormAppearance } from "../../../SupplierForm.types";

type RowNumber = number;
type IsSelected = boolean;

// 2 different states because the MRT library needs its own state !
export type RowSelectionState = {
    rowSelection: Record<RowNumber, IsSelected>;
    existingIngredientsMapping: Record<RowNumber, SupplierFormIngredientSupplier | undefined>;
    ingredientListMapping: Record<RowNumber, IngredientList>;
};

const getInitialSelection = (
    ingredientList: IngredientList[] | undefined,
    existingIngredients: SupplierFormIngredientSupplier[],
) => {
    const emptyRowSelectionState: RowSelectionState = {
        rowSelection: {},
        existingIngredientsMapping: {},
        ingredientListMapping: {},
    };

    if (ingredientList === undefined) return emptyRowSelectionState;

    return ingredientList.reduce((acc, ingredient, rowNumber) => {
        const foundExistingIng = existingIngredients.find((ing) => {
            return ing.ingredient["@id"] === ingredient["@id"];
        });
        const isFound = foundExistingIng !== undefined;

        acc["rowSelection"][rowNumber] = isFound;
        acc["existingIngredientsMapping"][rowNumber] = foundExistingIng;
        acc["ingredientListMapping"][rowNumber] = ingredient;

        return acc;
    }, emptyRowSelectionState);
};

type SupplierIngredientsGridProps = {
    appearance?: SupplierFormAppearance;
    existingIngredientSuppliers: SupplierFormIngredientSupplier[];
    setNewIngredientSuppliers: Dispatch<SetStateAction<SupplierFormIngredientSupplier[]>>;
    delayedSearch?: string;
};
export const SupplierIngredientsGrid = (props: SupplierIngredientsGridProps) => {
    const { t } = useTranslation();
    const { appearance = "legacy", existingIngredientSuppliers, setNewIngredientSuppliers, delayedSearch = "" } = props;

    const { company } = useCompanyMetadata();
    const { selectedCompany } = useFoodpilotContext();
    const [ingredientPage, setIngredientPage] = useState<MRT_PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    });

    // Reset to first page whenever the (debounced) search term changes,
    // otherwise we may stay on a page index that no longer exists in the
    // filtered result set.
    useEffect(() => {
        setIngredientPage((prev) => ({ ...prev, pageIndex: 0 }));
    }, [delayedSearch]);

    // Scoped by the selected company: the API only lists ingredients it owns or that are shared with it.
    const { ingredients, totalItems } = useGetAllIngredient({
        companyId: selectedCompany?.id,
        search: delayedSearch,
        params: {
            page: ingredientPage.pageIndex + 1,
            itemsPerPage: ingredientPage.pageSize,
        },
    });

    const initialSelection = getInitialSelection(ingredients, existingIngredientSuppliers);
    const [rowSelection, setRowSelection] = useState(initialSelection["rowSelection"]);

    useEffect(() => {
        setRowSelection(initialSelection["rowSelection"]);
    }, [ingredients]);

    useEffect(() => {
        // The RowSelection is a Record<RowNumber, ...>;
        // The problem comes from MRT actually deleting the key, instead of setting it to False
        // when we unselect the element.
        // So we want to make sure the keys who don't exist are still set to at least FALSE;
        const rowSelectionFixed = Array(ingredientPage.pageSize)
            .fill(undefined)
            .map((_, index) => index);
        const rowSelectionArray = rowSelectionFixed.map((row) => {
            const rowString = row.toString();
            const selectedRow = rowSelection[row];
            if (selectedRow !== undefined) {
                return [rowString, selectedRow];
            }
            return [rowString, false];
        });
        if (rowSelectionArray.length === 0) {
            // Means the component is not loaded yet.
            return;
        }

        const existingIngredientCopy = [...existingIngredientSuppliers];
        const newProductIngredients = rowSelectionArray.reduce((previousValue, [rowNumber, isSelected]) => {
            if (isSelected === false) {
                // Since it's NOT selected, we want to DELETE the item if it existed.
                const row = Number(rowNumber);
                const existingIng = initialSelection["existingIngredientsMapping"][row];
                if (existingIng === undefined) return previousValue;

                return previousValue.filter((item) => item.ingredient["@id"] !== existingIng.ingredient["@id"]);
            } else {
                // The item is actively selected.
                const row = Number(rowNumber);
                const existingIng = initialSelection["existingIngredientsMapping"][row];

                // Does it already exists ?
                if (existingIng !== undefined) return previousValue;

                // Now we're sure we just selected it.
                const ingredientList = initialSelection["ingredientListMapping"][row];

                const newIngredient: SupplierFormIngredientSupplier = {
                    ingredient: {
                        "@id": ingredientList["@id"],
                        name: ingredientList.name,
                        ingredientLine: ingredientList.ingredientLine,
                        ingredientReferenceId: ingredientList.ingredientReferenceId,
                        referenceSource: ingredientList.referenceSource,
                    },
                    quantity: null,
                    transportSelections: {},
                    transportSelectionsStatus: "new",
                    unit: null,
                };

                return [...previousValue, newIngredient];
            }
        }, existingIngredientCopy);

        // Here, we have the Full previous selected ingredients
        // In addition of the new ones;
        // In deletion of those that were deleted;
        setNewIngredientSuppliers(newProductIngredients);
    }, [rowSelection]);

    const columnHelper = createMRTColumnHelper<IngredientList>();
    const mrt_columns = [
        columnHelper.accessor((row) => row.name, {
            id: "name",
            header: t("menu.products.ingredient_name", "Ingredient name"),
            Cell: (value) => {
                const ing = value.row.original;
                const url = URLHandler.ingredient.information(company.id, ing.id);
                return <GridLink to={url} label={value.cell.getValue()} inNewTab />;
            },
        }),
        columnHelper.accessor((row) => row.ingredientLine?.name ?? "-", {
            id: "ingredientLine",
            header: t("menu.products.ingredient_line", "Ingredient line"),
        }),
        columnHelper.accessor((row) => row.ingredientSuppliersCount, {
            id: "ingredientSuppliersCount",
            header: t("menu.products.suppliers", "Suppliers"),
            Cell: (value) => {
                return <GridText value={value.cell.getValue()} />;
            },
        }),
    ];

    return (
        <AppMRTGrid
            appearance={appearance === "product-v2" ? "hub" : undefined}
            columns={mrt_columns}
            data={ingredients ?? []}
            enableColumnResizing={appearance === "product-v2" ? false : undefined}
            enableRowSelection={(row) => {
                const link = existingIngredientSuppliers.find((item) => item.ingredient["@id"] === row.original["@id"]);
                return (
                    link === undefined || selectedCompany === null || isLinkRemovableBy(link, selectedCompany["@id"])
                );
            }}
            enablePagination={true}
            enableStickyFooter={appearance === "product-v2" ? false : undefined}
            enableTableFooter={appearance === "product-v2" ? false : undefined}
            muiTableBodyRowProps={
                appearance === "product-v2" ?
                    ({ row }) => ({
                        onClick: (event) => {
                            const target = event.target as HTMLElement;
                            const isInteractiveElement = target.closest(
                                'button, a, input, label, select, textarea, [role="button"], [role="checkbox"], [role="link"], [data-action]',
                            );

                            if (isInteractiveElement !== null) return;

                            row.toggleSelected();
                        },
                        sx: {
                            cursor: "pointer",
                        },
                    })
                :   undefined
            }
            onRowSelectionChange={setRowSelection}
            state={{
                isLoading: ingredients === undefined,
                rowSelection: rowSelection,
                pagination: ingredientPage,
                globalFilter: delayedSearch,
            }}
            rowCount={totalItems ?? 0}
            manualPagination
            manualFiltering
            onPaginationChange={setIngredientPage}
        />
    );
};
